import React, { useState, useEffect } from "react";
import { Button, Form, Input, Modal, Popconfirm, Space, Tag, Tooltip, Typography, Upload } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { DeleteOutlined, DownloadOutlined, FileOutlined, InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { ClinicDocument } from "../../../types/file/ClinicDocumentTypes";
import {
	getClinicDocuments,
	uploadClinicDocument,
	downloadClinicDocument,
	deleteClinicDocument,
} from "../../../api/file/clinicDocumentsApi";
import { DataTable, SectionCard } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";

const { Text } = Typography;

const ACCEPTED_TYPES = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const MAX_SIZE_MB = 20;

const ClinicDocumentStorage: React.FC = () => {
	const { showError, showSuccess } = useNotifications();
	const [documents, setDocuments] = useState<ClinicDocument[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [downloadingId, setDownloadingId] = useState<string | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [uploading, setUploading] = useState<boolean>(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [form] = Form.useForm();

	useEffect(() => {
		fetchDocuments();
	}, []);

	const fetchDocuments = async () => {
		setLoading(true);
		try {
			const data = await getClinicDocuments();
			setDocuments(data || []);
		} catch (error) {
			console.error("Error fetching clinic documents:", error);
			showError("Failed to load documents");
		} finally {
			setLoading(false);
		}
	};

	const handleBeforeUpload = (file: File): boolean => {
		if (file.size > MAX_SIZE_MB * 1024 * 1024) {
			showError(`File is too large (max ${MAX_SIZE_MB} MB)`);
			return false;
		}
		setSelectedFile(file);
		// Default the document name to the file name (without extension) if empty
		if (!form.getFieldValue("name")) {
			form.setFieldsValue({ name: file.name.replace(/\.[^/.]+$/, "") });
		}
		return false; // Prevent automatic upload
	};

	const handleUpload = async () => {
		let values;
		try {
			values = await form.validateFields();
		} catch {
			return;
		}
		if (!selectedFile) {
			showError("Please select a file to upload");
			return;
		}

		setUploading(true);
		try {
			const user = getSessionUser();
			await uploadClinicDocument({
				name: values.name,
				description: values.description,
				category: values.category,
				userId: user.id,
				file: selectedFile,
			});
			showSuccess("Document uploaded");
			closeModal();
			fetchDocuments();
		} catch (error) {
			console.error("Error uploading document:", error);
			showError("Failed to upload document");
		} finally {
			setUploading(false);
		}
	};

	const closeModal = () => {
		setModalOpen(false);
		setSelectedFile(null);
		form.resetFields();
	};

	const handleDownload = async (document: ClinicDocument) => {
		setDownloadingId(document.id);
		try {
			await downloadClinicDocument(document.id, document.file_name);
		} catch (error) {
			console.error("Error downloading document:", error);
			showError("Failed to download document");
		} finally {
			setDownloadingId(null);
		}
	};

	const handleDelete = async (document: ClinicDocument) => {
		setDeletingId(document.id);
		try {
			await deleteClinicDocument(document.id);
			showSuccess("Document deleted");
			fetchDocuments();
		} catch (error) {
			console.error("Error deleting document:", error);
			showError("Failed to delete document");
		} finally {
			setDeletingId(null);
		}
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / 1048576).toFixed(1)} MB`;
	};

	const columns = [
		{
			title: "Name",
			key: "name",
			render: (_: unknown, record: ClinicDocument) => (
				<div>
					<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<FileOutlined style={{ color: "#1677ff" }} />
						<Text strong>{record.name}</Text>
					</span>
					<Text type="secondary" style={{ fontSize: 12 }}>{record.file_name}</Text>
				</div>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			render: (v: string | null) => v ? <Tag>{v}</Tag> : <Text type="secondary">—</Text>,
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			ellipsis: true,
			render: (v: string | null) => v || <Text type="secondary">—</Text>,
		},
		{
			title: "Size",
			dataIndex: "file_size",
			key: "file_size",
			render: (v: number) => formatFileSize(v),
		},
		{
			title: "",
			key: "actions",
			render: (_: unknown, record: ClinicDocument) => (
				<Space size={2}>
					<Tooltip title="Download">
						<Button
							type="text"
							size="small"
							icon={<DownloadOutlined />}
							loading={downloadingId === record.id}
							onClick={(e) => { e.stopPropagation(); handleDownload(record); }}
						/>
					</Tooltip>
					<Popconfirm
						title="Delete this document?"
						description="This cannot be undone."
						okText="Delete"
						okButtonProps={{ danger: true }}
						onConfirm={() => handleDelete(record)}
					>
						<Tooltip title="Delete">
							<Button
								type="text"
								size="small"
								danger
								icon={<DeleteOutlined />}
								loading={deletingId === record.id}
								onClick={(e) => e.stopPropagation()}
							/>
						</Tooltip>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<SectionCard
			title="Document Storage"
			subtitle="Upload and store your clinic's compliance and regulatory documents"
			extra={
				<Button type="primary" icon={<UploadOutlined />} onClick={() => setModalOpen(true)}>
					Upload Document
				</Button>
			}
			noPadding
		>
			<DataTable
				columns={columns}
				dataSource={documents}
				rowKey="id"
				loading={loading}
				pagination={{ pageSize: 10 }}
				emptyTitle="No documents yet"
				emptyDescription="Upload your clinic's compliance and regulatory documents to keep them here"
				bare
			/>

			<Modal
				title="Upload Document"
				open={modalOpen}
				onCancel={closeModal}
				onOk={handleUpload}
				okText="Upload"
				confirmLoading={uploading}
				closable={!uploading}
			>
				<Form form={form} layout="vertical" disabled={uploading}>
					<Form.Item
						name="name"
						label="Name"
						rules={[{ required: true, message: "Please enter a document name" }]}
					>
						<Input placeholder="e.g. Professional Indemnity Insurance" />
					</Form.Item>
					<Form.Item name="category" label="Category">
						<Input placeholder="e.g. Compliance, Regulatory, Policy" />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={3} placeholder="Optional notes about this document" />
					</Form.Item>
					<Form.Item label="File" required>
						<Upload.Dragger
							accept={ACCEPTED_TYPES}
							beforeUpload={handleBeforeUpload}
							onRemove={() => setSelectedFile(null)}
							maxCount={1}
							fileList={selectedFile ? [{ uid: "-1", name: selectedFile.name } as UploadFile] : []}
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">Click or drag a file to upload</p>
							<p className="ant-upload-hint">PDF, Word document, or image (max {MAX_SIZE_MB} MB)</p>
						</Upload.Dragger>
					</Form.Item>
				</Form>
			</Modal>
		</SectionCard>
	);
};

export default ClinicDocumentStorage;
